import { Colors, Icon, Spinner, Text } from '@amelio/ui-library';

import type { PillarSummary } from '@/types/objectives';

import PillarCarousel from '@/components/objectives/PillarCarousel';
import PillarSummaryCard from '@/components/objectives/PillarSummaryCard';
import { useAmelioTranslations } from '@/hooks';

interface PillarContributionsProps {
    summaries: PillarSummary[];
    isKnown: boolean;
    isError: boolean;
    /** The pillars the reader holds at least one objective under, in the selected period. */
    contributingPillarIds: Set<string>;
}

/**
 * How far each strategic pillar has come across the company, each tile marked when the employee
 * contributes to it. A company with no pillar has nothing to show here, so the section stays out of
 * the page rather than announcing an empty strategy to an employee who cannot define one.
 *
 * The tiles are for reading only: they count the whole company, while the pillar page lists what
 * its reader may open, so a tile leading there would show the same pillar under another figure.
 */
export const PillarContributions = ({
    summaries,
    isKnown,
    isError,
    contributingPillarIds,
}: PillarContributionsProps) => {
    const { t } = useAmelioTranslations();

    if (isKnown && !isError && summaries.length === 0) {
        return null;
    }

    const heading = (
        <div>
            <h2
                id={'employee-portal-pillars-title'}
                className={'text-navy text-[20px] font-medium'}
            >
                {t('employeePortal.objectives.pillars.title')}
            </h2>

            <Text color={Colors.BASE_400}>
                {t('employeePortal.objectives.pillars.subtitle', {
                    badge: t('employeePortal.objectives.pillars.contributing'),
                })}
            </Text>
        </div>
    );

    const renderContent = () => {
        if (isError) {
            return (
                <>
                    {heading}

                    <p
                        role={'alert'}
                        className={'text-error text-[14px]'}
                    >
                        {t('employeePortal.objectives.pillars.loadError')}
                    </p>
                </>
            );
        }

        if (!isKnown) {
            return (
                <>
                    {heading}

                    <div className={'flex justify-center py-6'}>
                        <Spinner
                            size={'md'}
                            color={Colors.PRIMARY}
                            dataTestId={'employee-portal-pillars-loading'}
                        />
                    </div>
                </>
            );
        }

        return (
            <PillarCarousel
                items={summaries}
                getKey={(pillar) => pillar.id}
                ariaLabel={t('employeePortal.objectives.pillars.title')}
                header={heading}
                renderItem={(pillar) => (
                    <PillarSummaryCard
                        pillar={pillar}
                        linksToPillarPage={false}
                        progressionLabel={t('employeePortal.objectives.pillars.progression')}
                        badge={
                            contributingPillarIds.has(pillar.id) && (
                                <span
                                    className={
                                        'text-navy inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold'
                                    }
                                    data-testid={`employee-portal-pillar-${pillar.id}-contributing`}
                                >
                                    <Icon
                                        name={'lucide-sparkles'}
                                        size={12}
                                        color={'inherit'}
                                        ariaHidden
                                    />

                                    {t('employeePortal.objectives.pillars.contributing')}
                                </span>
                            )
                        }
                    />
                )}
            />
        );
    };

    return (
        <section
            aria-labelledby={'employee-portal-pillars-title'}
            className={'flex flex-col gap-5'}
            data-testid={'employee-portal-pillars'}
        >
            {renderContent()}
        </section>
    );
};
